import { useGetBlobSasToken } from './useGetBlobSasToken';
import { BlobServiceClient } from '@azure/storage-blob';
import { v4 as uuidv4 } from 'uuid';
import type { BlockBlobClient } from '@azure/storage-blob';
import React from 'react';
import { FileRejection } from 'react-dropzone';

export interface UploadType {
  file: File;
  fileTypeTag: string;
  fileName: string;
  blockBlobClient: BlockBlobClient | undefined;
  progress: number;
  isSuccessful: boolean;
  isError: boolean;
  errorMessage?: string;
  isUploading: boolean;
  url: string | undefined;
}

export function useBlobStorage(blobContainerName: string) {
  const [uploads, setUploads] = React.useState<UploadType[]>([]);
  const [imageUrl, setImageUrl] = React.useState('');
  const { refetch: fetchSasToken } = useGetBlobSasToken(blobContainerName);

  const getBlockBlobClient = React.useCallback(
    async (fileName: string) => {
      const { data } = await fetchSasToken();
      const { containerName, token, containerBaseURL } =
        data?.data.attributes || {};
      if (!containerName) {
        return;
      }
      const blobServiceClient = new BlobServiceClient(
        `${containerBaseURL}?${token}`
      );
      const containerClient =
        blobServiceClient.getContainerClient(containerName);

      return containerClient.getBlockBlobClient(fileName);
    },
    [fetchSasToken]
  );

  const handleUploadToBlob = React.useCallback(
    async (upload: UploadType, updateLocalUploads = true) => {
      const uploadResponse = await upload.blockBlobClient?.uploadData(
        upload.file,
        {
          concurrency: 5,
          onProgress: (progressEvent) => {
            const newProgress = Math.floor(
              (progressEvent.loadedBytes / upload.file.size) * 100
            );
            if (updateLocalUploads) {
              setUploads((prevUploads) => {
                return prevUploads.map((prevUpload) => {
                  return prevUpload.blockBlobClient?.['_name'] ===
                    upload.blockBlobClient?.['_name']
                    ? {
                        ...prevUpload,
                        progress: newProgress,
                      }
                    : prevUpload;
                });
              });
            }
          },
        }
      );
      if (updateLocalUploads) {
        setUploads((prevUploads) => {
          return prevUploads.map((prevUpload) => {
            return prevUpload.file.name === upload.file.name
              ? {
                  ...prevUpload,
                  isSuccessful: uploadResponse?.['_response'].status === 201,
                  isError: uploadResponse?.['_response'].status !== 201,
                  isUploading: false,
                }
              : prevUpload;
          });
        });
      }
    },
    [setUploads]
  );

  const handleFileUpload = React.useCallback(
    async (
      acceptedFiles: File[],
      fileRejections: FileRejection[] = [],
      updateLocalUploads = true
    ) => {
      const uploadPromises = acceptedFiles.map(async (file) => {
        // file extension to append to the file guid
        const fileType = file.name?.split('.');
        const blockBlobClient = await getBlockBlobClient(
          `${uuidv4()}.${fileType[fileType.length - 1]}`
        );
        const fileName = blockBlobClient?.['_name'];

        return {
          file,
          blockBlobClient,
          fileName,
          progress: 0,
          isSuccessful: false,
          isError: false,
          url: blockBlobClient?.url.split('?')[0],
          fileTypeTag: fileType[fileType.length - 1],
          isUploading: true,
        };
      });
      const rejectedUploads: UploadType[] = fileRejections.map((rejection) => {
        const fileType = rejection.file.name?.split('.');
        const error = rejection.errors[0];
        const errorMessage =
          error.code === 'file-too-large'
            ? 'File is larger than 15MBs'
            : error.message;
        return {
          file: rejection.file,
          fileName: rejection.file.name || uuidv4(),
          blockBlobClient: undefined,
          url: undefined,
          progress: 0,
          isSuccessful: false,
          isError: true,
          errorMessage: errorMessage,
          fileTypeTag: fileType[fileType.length - 1],
          isUploading: false,
        };
      });
      // collect the new uploads with blob client
      const newUploads: UploadType[] = await Promise.all(uploadPromises);
      if (updateLocalUploads) {
        setUploads([...uploads, ...newUploads, ...rejectedUploads]);
      }

      try {
        await Promise.all(
          newUploads.map((upload) =>
            handleUploadToBlob(upload, updateLocalUploads)
          )
        );
        return newUploads.filter((upload): upload is UploadType => !!upload);
      } catch (error) {
        console.error('Error uploading files', error);
      }
    },
    [getBlockBlobClient, handleUploadToBlob, uploads]
  );

  //this will deleted the files from upload state only
  const handleDelete = React.useCallback(async (fileName: string) => {
    setUploads((prevUploads) => {
      return prevUploads.filter((prevUpload) => {
        return prevUpload.fileName !== fileName;
      });
    });
  }, []);

  const readImage = async (blobUrl: string, disable: boolean = true) => {
    if (
      !blobUrl ||
      disable ||
      !uploads.length ||
      uploads[uploads.length - 1].url !== blobUrl
    ) {
      return;
    }
    const fileName = blobUrl.split('/').slice(-1)[0];

    try {
      const blockBlobClient = await getBlockBlobClient(fileName);
      const downloadResponse = await blockBlobClient?.download();
      const blobData = await downloadResponse?.blobBody;
      if (!blobData) {
        return;
      }
      // Convert the blob data to a URL
      const objectURL = URL.createObjectURL(blobData);

      setImageUrl(objectURL);
      return objectURL;
    } catch (error: any) {
      console.error('Error:', error.message);
    }
  };

  return {
    handleFileUpload,
    handleDelete,
    uploads,
    readImage,
    imageUrl,
    getBlockBlobClient,
    setUploads,
  };
}
